// 签到 / 金币余额 / VIP 状态相关接口
//
// 对应 Android MainJZApiUserLoader.kt 中的 sign/v4 系列 + account/balance

import { apiGet, apiPost } from "./client";

/** 一天的签到状态 */
export interface SignDay {
  /** 周几（1-7） */
  week_day: number;
  /** 上限可领的游戏分钟数 */
  max_reward_time: number;
  /** 每完成 step 单位任务奖励 */
  step: number;
  /** 今日任务最大次数 */
  task_max: number;
  /** 签到状态 0=未签到 1=已签 2=漏签可补 */
  status?: number;
}

export interface SignState {
  /** 当前可领的签到任务 id */
  sign_id: number;
  /** 按钮文案，比如 "签到" / "已签到" */
  button: string;
  /** 今日已完成进度 */
  finish_num: number;
  /** 7 天奖励配置 */
  reward_time: SignDay[];
  /** 顶部说明文本，例如 "已领取X分钟,还可领取Y分钟" */
  reward_content?: string;
  /** 今日是周几 1-7 */
  today: number;
}

export interface AccountBalance {
  /** 当前余额（可能是字符串） */
  balance: string;
  /** 本月将过期数量 */
  balance_month_expire?: string;
}

/** 拉签到面板信息 */
export function fetchSignState() {
  return apiGet<SignState>("/api/v1/client/user/sign/v4");
}

/** 提交签到 */
export function postSignIn(signId: number) {
  return apiPost<{ reward?: number; balance?: string }>(
    "/api/v1/client/user/signin/v4",
    { sign_id: signId },
  );
}

/** 补签 */
export function postReSign(retroactiveDay: number) {
  return apiPost<{ reward?: number }>("/api/v1/client/user/resign/v4", {
    retroactive_day: retroactiveDay,
  });
}

/** 拉金币余额。account_type=1 = 金币（游戏分钟数） */
export function fetchBalance(accountType = 1) {
  return apiGet<AccountBalance>("/api/v1/client/user/account/balance", {
    account_type: accountType,
  });
}

/** 某游戏的专属可玩时长（对齐 Android MainJZUserTimeResponse.PlayTime） */
export interface UserGamePlayTime {
  game_id: number;
  /** 该游戏专属游戏时长（分钟） */
  play_time: number;
  no_queue_time?: number;
  is_forver_play_time?: number;
  is_forever_no_queue_time?: number;
  game_name?: string;
  image?: string;
}

/** 用户游戏时长（对齐 Android GET /user/time → MainJZUserTimeResponse.UserTimeBalance） */
export interface UserGameTime {
  /** 通用游戏时长余额（分钟，字符串） */
  balance: string;
  /** 剩余时长提醒（message/time_left 等） */
  reminder?: {
    message: string;
    category: number;
    sub_message: string;
    time_left: string;
  };
  /** 按游戏的专属时长列表 */
  play_time?: UserGamePlayTime[];
}

/** 拉用户对某游戏的可玩时长：通用 balance + 该游戏专属 play_time。
 *  对齐 Android MainJZApiUserLoader.checkUserGameTime（GET /user/time?game_id=）。
 *  game_id=0 只看通用余额。开始游戏前判断"有没有时长"用。 */
export function fetchUserGameTime(gameId: number | string = 0) {
  return apiGet<UserGameTime>("/api/v1/client/user/time", { game_id: gameId });
}

// ============ VIP 状态判断 ============
//
// 后端 user info 用三个字段一起判（对齐 Android LianYunUserVipLoginModel）：
//   vip_type:  0=普通  1=VIP  2=SVIP
//   status:    1=正常  0=已过期
//   expire_time / vip_validity_time:  "YYYY-MM-DD HH:mm:ss" 字符串
//
// 老接口可能用 vip 字符串字段（"0"/"1"/"2"），兼容一下。

export type VipLevel = "none" | "vip" | "svip";

/** 综合状态：含过期态 */
export type VipStatus =
  | "none" // 从未开通
  | "vip" // VIP 有效
  | "svip" // SVIP 有效
  | "vip_expired" // 曾是 VIP 但已过期
  | "svip_expired"; // 曾是 SVIP 但已过期

interface VipBearer {
  vip?: string | number | null;
  vip_type?: number | string | null;
  status?: number | string | null;
  /** 不同接口字段名：getuserinfobytoken 用 vip_validity_time，VIP 子模型用 expire_time */
  vip_validity_time?: string | null;
  expire_time?: string | null;
}

/** 把后端的 vip 字段（"0"/"1"/"2"或数字）转成枚举。仅看等级，不看是否过期。 */
export function parseVipLevel(raw: unknown): VipLevel {
  if (typeof raw === "object" && raw !== null) {
    const o = raw as VipBearer;
    // vip_type 优先（更新的字段）
    if (o.vip_type !== undefined && o.vip_type !== null) {
      const t = Number(o.vip_type);
      if (t === 2) return "svip";
      if (t === 1) return "vip";
      return "none";
    }
    // fallback 看 vip 字段
    if (o.vip !== undefined && o.vip !== null) {
      const v = String(o.vip);
      if (v === "2") return "svip";
      if (v === "1") return "vip";
    }
    return "none";
  }
  // 直接传字符串/数字的兼容路径
  const v = String(raw ?? "0");
  if (v === "2") return "svip";
  if (v === "1") return "vip";
  return "none";
}

/** 解析时间字符串成 Date（兼容多种格式）。无效返回 null。 */
function parseExpireDate(s?: string | null): Date | null {
  if (!s) return null;
  // 后端用 "YYYY-MM-DD HH:mm:ss"，Safari 不认空格分隔，替换成 T
  const iso = s.includes("T") ? s : s.replace(" ", "T");
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/** 是否已过期。优先看 status 字段（最权威），fallback 比较 expire 时间。 */
export function isVipExpired(raw: VipBearer | null | undefined): boolean {
  if (!raw) return true;
  // 有 status 字段直接看
  if (raw.status !== undefined && raw.status !== null) {
    return Number(raw.status) !== 1;
  }
  // 没有 status 就靠 expire_time
  const expire = parseExpireDate(raw.expire_time ?? raw.vip_validity_time);
  if (!expire) return false; // 没字段且没过期信息 → 当作未过期，让上游 vip_type 决定
  return expire.getTime() < Date.now();
}

/** 综合 VIP 状态：会员等级 × 过期判断 */
export function getVipStatus(raw: VipBearer | null | undefined): VipStatus {
  const level = parseVipLevel(raw ?? null);
  if (level === "none") return "none";
  if (isVipExpired(raw)) return level === "svip" ? "svip_expired" : "vip_expired";
  return level;
}

/** 是否当前为有效高级用户（VIP 或 SVIP，未过期）。对应 Gamino doc isPremium */
export function isPremium(raw: VipBearer | null | undefined): boolean {
  const s = getVipStatus(raw);
  return s === "vip" || s === "svip";
}

/** 是否为有效 SVIP（部分高级游戏 corner_mark=1 要求 SVIP） */
export function isSvip(raw: VipBearer | null | undefined): boolean {
  return getVipStatus(raw) === "svip";
}

/** 距过期剩余天数（向下取整）。无 expire 信息或已过期返 null。 */
export function vipDaysLeft(raw: VipBearer | null | undefined): number | null {
  if (!raw) return null;
  if (isVipExpired(raw)) return 0;
  const expire = parseExpireDate(raw.expire_time ?? raw.vip_validity_time);
  if (!expire) return null;
  const ms = expire.getTime() - Date.now();
  return Math.max(0, Math.floor(ms / 86400_000));
}

/** 即将过期：剩余 <= 7 天（且未过期），用来弹续费提示 */
export function isVipExpiringSoon(
  raw: VipBearer | null | undefined,
  withinDays = 7,
): boolean {
  const d = vipDaysLeft(raw);
  return d !== null && d > 0 && d <= withinDays;
}

// ============ 游戏 corner_mark 鉴权 ============

/** 游戏的 corner_mark 含义：
 *   1   = SVIP 专享
 *   其他非 0 值 = VIP 及以上可玩
 *   0 / null = 无门槛 */
export type GameAccess = "free" | "vip" | "svip";

export function gameAccessLevel(corner_mark: unknown): GameAccess {
  if (corner_mark === undefined || corner_mark === null) return "free";
  const m = Number(corner_mark);
  if (m === 1) return "svip";
  if (m !== 0) return "vip";
  return "free";
}

/** 当前用户是否能玩这个游戏（不考虑排队，只看会员门槛） */
export function canAccessGame(
  user: VipBearer | null | undefined,
  cornerMark: unknown,
): boolean {
  const required = gameAccessLevel(cornerMark);
  if (required === "free") return true;
  const status = getVipStatus(user);
  if (required === "svip") return status === "svip";
  // vip：VIP 或 SVIP 都行
  return status === "vip" || status === "svip";
}
